// services/messageService.js
import supabase from '../supabase';
import emailService from './emailService';

// Función auxiliar para formatear el nombre del remitente/destinatario
function formatUserName(profile, defaultEmail = "") {
  if (!profile) return defaultEmail || "Usuario";
  
  // Obtener nombre y apellido, o usar cadenas vacías si son nulos
  const firstName = profile.first_name || "";
  const lastName = profile.last_name || "";
  
  // Combinar y eliminar espacios extra
  const fullName = `${firstName} ${lastName}`.trim();
  
  // Si no hay nombre completo, usar email o "Usuario"
  return fullName || profile.email || defaultEmail || "Usuario";
}

export const messageService = {
  // Enviar mensaje con validaciones mejoradas
  async sendMessage(recipientId, subject, content) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Usuario no autenticado');

    try {
      console.log('🔄 Iniciando envío de mensaje...', { recipientId, subject: subject.substring(0, 50) });

      // Verificar si es una respuesta a una conversación existente
      const { data: existingMessages } = await supabase
        .from('messages')
        .select('id')
        .eq('sender_id', recipientId)
        .eq('recipient_id', user.id)
        .limit(1);

      const isReply = existingMessages && existingMessages.length > 0;
      console.log('📝 Es respuesta:', isReply);
      
      // Solo verificar límites y reglas si NO es una respuesta
      if (!isReply) {
        console.log('🔍 Verificando reglas de negocio...');
        
        // Obtener perfil del remitente
        const { data: senderProfile, error: senderError } = await supabase
          .from('user_profiles')
          .select('id, membership_level')
          .eq('id', user.id)
          .maybeSingle();

        if (senderError) {
          console.error('Error al obtener perfil del remitente:', senderError);
        }

        // Verificar si el remitente es empresa
        const { data: senderCompany } = await supabase
          .from('company_profiles')
          .select('id')
          .eq('id', user.id)
          .maybeSingle();

        const senderType = senderProfile ? 'individual' : (senderCompany ? 'company' : 'unknown');
        console.log('👤 Tipo de remitente:', senderType);

        // Obtener tipo del destinatario
        const { data: recipientProfile } = await supabase
          .from('user_profiles')
          .select('id')
          .eq('id', recipientId)
          .maybeSingle();
        
        const { data: recipientCompany } = await supabase
          .from('company_profiles')
          .select('id')
          .eq('id', recipientId)
          .maybeSingle();
        
        const recipientType = recipientProfile ? 'individual' : (recipientCompany ? 'company' : 'unknown');
        console.log('👥 Tipo de destinatario:', recipientType);
        
        // Verificar reglas de negocio
        if (senderType === 'individual' && recipientType === 'company') {
          throw new Error('Los usuarios individuales no pueden iniciar conversaciones con empresas');
        }
        
        // Verificar límite diario (solo para usuarios individuales)
        if (senderType === 'individual') {
          console.log('⏰ Verificando límite diario...');
          
          const today = new Date().toISOString().split('T')[0];
          const { data: todayMessages, error: limitError } = await supabase
            .from('messages')
            .select('recipient_id')
            .eq('sender_id', user.id)
            .gte('created_at', `${today}T00:00:00`)
            .lte('created_at', `${today}T23:59:59`);
          
          if (limitError) {
            console.error('Error al verificar límite diario:', limitError);
            // Continuar sin verificar límite si hay error
          } else {
            // Contar destinatarios únicos
            const uniqueRecipients = new Set(todayMessages?.map(msg => msg.recipient_id) || []);
            console.log('📊 Destinatarios únicos hoy:', uniqueRecipients.size);
            
            if (uniqueRecipients.size >= 5 && !uniqueRecipients.has(recipientId)) {
              throw new Error('Has alcanzado el límite diario de 5 destinatarios diferentes');
            }
          }
        }
      }

      // Preparar datos del mensaje
      const messageData = {
        sender_id: user.id,
        recipient_id: recipientId,
        subject: subject.trim(),
        content: content.trim()
      };

      console.log('📤 Enviando mensaje...', messageData);

      // Enviar mensaje
      const { data, error } = await supabase
        .from('messages')
        .insert([messageData])
        .select()
        .single();

      if (error) {
        console.error('❌ Error al insertar mensaje:', error);
        throw error;
      }

      console.log('✅ Mensaje enviado exitosamente:', data.id);

      // Notificar al destinatario por email (no bloqueante: nunca rompe el envío)
      emailService.notifyNewMessage(data.id).catch(() => {});

      return data;

    } catch (error) {
      console.error('💥 Error en sendMessage:', error);
      
      // Manejar errores específicos
      if (error.code === '42501') {
        throw new Error('No tienes permisos para enviar mensajes. Verifica que tu cuenta esté activa.');
      } else if (error.code === '23505') {
        throw new Error('Error de duplicación. Espera un momento antes de enviar otro mensaje.');
      } else if (error.message.includes('limit')) {
        throw error; // Re-lanzar errores de límite tal como están
      } else {
        throw new Error('Error al enviar el mensaje: ' + (error.message || 'Error desconocido'));
      }
    }
  },

  // Obtener bandeja de entrada
  async getInbox(page = 1, pageSize = 20) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Usuario no autenticado');

    const start = (page - 1) * pageSize;
    const end = start + pageSize - 1;

    try {
      console.log('📬 Obteniendo bandeja de entrada...', { page, pageSize });

      // Obtener mensajes
      const { data: messages, error, count } = await supabase
        .from('messages')
        .select('*', { count: 'exact' })
        .eq('recipient_id', user.id)
        .eq('recipient_deleted', false)
        .order('created_at', { ascending: false })
        .range(start, end);

      if (error) {
        console.error('Error al obtener mensajes:', error);
        throw error;
      }

      // Si no hay mensajes, retornar vacío
      if (!messages || messages.length === 0) {
        return {
          messages: [],
          totalCount: 0,
          totalPages: 0,
          currentPage: page
        };
      }

      // Obtener IDs únicos de remitentes
      const senderIds = [...new Set(messages.map(msg => msg.sender_id))];

      // Obtener perfiles de usuarios con email incluido
      const { data: userProfiles, error: userError } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, profile_image, email')
        .in('id', senderIds);

      if (userError) {
        console.error('Error al obtener perfiles de usuarios:', userError);
      }

      // Obtener perfiles de empresas
      const { data: companyProfiles, error: companyError } = await supabase
        .from('company_profiles')
        .select('id, company_name, logo, email')
        .in('id', senderIds);

      if (companyError) {
        console.error('Error al obtener perfiles de empresas:', companyError);
      }

      // Formatear mensajes con información del remitente
      const formattedMessages = messages.map(msg => {
        const userProfile = userProfiles?.find(p => p.id === msg.sender_id);
        const companyProfile = companyProfiles?.find(p => p.id === msg.sender_id);

        let sender;
        if (userProfile) {
          const displayName = formatUserName(userProfile);
          
          sender = {
            id: userProfile.id,
            name: displayName,
            email: userProfile.email,
            image: userProfile.profile_image,
            type: 'individual'
          };
        } else if (companyProfile) {
          sender = {
            id: companyProfile.id,
            name: companyProfile.company_name || "Empresa",
            email: companyProfile.email,
            image: companyProfile.logo,
            type: 'company'
          };
        } else {
          sender = {
            id: msg.sender_id,
            name: "Usuario desconocido",
            email: null,
            image: null,
            type: 'unknown'
          };
        }

        return {
          ...msg,
          sender
        };
      });

      console.log('✅ Bandeja de entrada obtenida:', formattedMessages.length, 'mensajes');

      return {
        messages: formattedMessages,
        totalCount: count || 0,
        totalPages: Math.ceil((count || 0) / pageSize),
        currentPage: page
      };
    } catch (error) {
      console.error('Error en getInbox:', error);
      throw error;
    }
  },

  // Obtener mensajes enviados
  async getSentMessages(page = 1, pageSize = 20) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Usuario no autenticado');

    const start = (page - 1) * pageSize;
    const end = start + pageSize - 1;

    try {
      // Obtener mensajes
      const { data: messages, error, count } = await supabase
        .from('messages')
        .select('*', { count: 'exact' })
        .eq('sender_id', user.id)
        .eq('sender_deleted', false)
        .order('created_at', { ascending: false })
        .range(start, end);

      if (error) throw error;

      // Si no hay mensajes, retornar vacío
      if (!messages || messages.length === 0) {
        return {
          messages: [],
          totalCount: 0,
          totalPages: 0,
          currentPage: page
        };
      }

      // Obtener IDs únicos de destinatarios
      const recipientIds = [...new Set(messages.map(msg => msg.recipient_id))];
      
      // Obtener perfiles de usuarios con email incluido
      const { data: userProfiles } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, profile_image, email')
        .in('id', recipientIds);

      // Obtener perfiles de empresas
      const { data: companyProfiles } = await supabase
        .from('company_profiles')
        .select('id, company_name, logo, email')
        .in('id', recipientIds);

      // Formatear mensajes con información del destinatario
      const formattedMessages = messages.map(msg => {
        const userProfile = userProfiles?.find(p => p.id === msg.recipient_id);
        const companyProfile = companyProfiles?.find(p => p.id === msg.recipient_id);
        
        let recipient;
        if (userProfile) {
          const displayName = formatUserName(userProfile);
          
          recipient = {
            id: userProfile.id,
            name: displayName,
            email: userProfile.email,
            image: userProfile.profile_image,
            type: 'individual'
          };
        } else if (companyProfile) {
          recipient = {
            id: companyProfile.id,
            name: companyProfile.company_name || "Empresa",
            email: companyProfile.email,
            image: companyProfile.logo,
            type: 'company'
          };
        } else {
          recipient = {
            id: msg.recipient_id,
            name: "Usuario desconocido",
            email: null,
            image: null,
            type: 'unknown'
          };
        }

        return {
          ...msg,
          recipient
        };
      });

      return {
        messages: formattedMessages,
        totalCount: count || 0,
        totalPages: Math.ceil((count || 0) / pageSize),
        currentPage: page
      };
    } catch (error) {
      console.error('Error en getSentMessages:', error);
      throw error;
    }
  },

  // Marcar mensaje como leído
  async markAsRead(messageId) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Usuario no autenticado');

    try {
      const { data, error } = await supabase
        .from('messages')
        .update({ 
          is_read: true,
          read_at: new Date().toISOString()
        })
        .eq('id', messageId)
        .eq('recipient_id', user.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error al marcar como leído:', error);
      throw error;
    }
  },

  // Eliminar mensaje (soft delete)
  async deleteMessage(messageId) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Usuario no autenticado');

    try {
      // Primero obtener el mensaje para saber si somos remitente o destinatario
      const { data: message, error: getError } = await supabase
        .from('messages')
        .select('sender_id, recipient_id')
        .eq('id', messageId)
        .single();

      if (getError) throw getError;

      const updateData = {};
      if (message.sender_id === user.id) {
        updateData.sender_deleted = true;
      } else if (message.recipient_id === user.id) {
        updateData.recipient_deleted = true;
      } else {
        throw new Error('No tienes permiso para eliminar este mensaje');
      }

      const { data, error } = await supabase
        .from('messages')
        .update(updateData)
        .eq('id', messageId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error al eliminar mensaje:', error);
      throw error;
    }
  },

  // Obtener contador de mensajes no leídos
  async getUnreadCount() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Usuario no autenticado');

    try {
      const { count, error } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .eq('recipient_id', user.id)
        .eq('recipient_deleted', false)
        .eq('is_read', false);

      if (error) throw error;
      return count || 0;
    } catch (error) {
      console.error('Error al obtener contador no leídos:', error);
      return 0;
    }
  },

  // Obtener límite diario restante
  async getRemainingDailyLimit() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Usuario no autenticado');

    try {
      // Verificar si es empresa (las empresas no tienen límite)
      const { data: companyProfile } = await supabase
        .from('company_profiles')
        .select('id')
        .eq('id', user.id)
        .maybeSingle();

      if (companyProfile) {
        return 999; // Sin límite para empresas
      }

      // Obtener la fecha de hoy en formato ISO
      const today = new Date().toISOString().split('T')[0];

      // Consultar mensajes enviados hoy
      const { data: todayMessages, error } = await supabase
        .from('messages')
        .select('recipient_id')
        .eq('sender_id', user.id)
        .gte('created_at', `${today}T00:00:00`)
        .lte('created_at', `${today}T23:59:59`);

      if (error) throw error;

      // Contar destinatarios únicos
      const uniqueRecipients = new Set(todayMessages?.map(msg => msg.recipient_id) || []);
      const currentCount = uniqueRecipients.size;

      // Calcular el límite restante
      return Math.max(0, 5 - currentCount);
    } catch (error) {
      console.error('Error al obtener el límite diario restante:', error);
      return 5; // Retornar límite completo en caso de error
    }
  },

  // Enviar mensaje de postulación laboral
  async sendJobApplicationMessage(jobId, recipientId, subject, content) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Usuario no autenticado');
  
    try {
      console.log('📋 Enviando mensaje de postulación laboral...');
      
      // Obtener el perfil del usuario para incluir su nombre
      const { data: userProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('first_name, last_name')
        .eq('id', user.id)
        .single();
      
      if (profileError) {
        console.error('Error al obtener el perfil del usuario:', profileError);
      }
      
      // Formatear el asunto para incluir el nombre del remitente
      const formattedSubject = userProfile 
        ? `${subject} - De: ${userProfile.first_name} ${userProfile.last_name}`
        : subject;
      
      // Enviar el mensaje usando la función normal (que ya maneja todas las validaciones)
      return await this.sendMessage(recipientId, formattedSubject, content);
    } catch (error) {
      console.error('Error al enviar mensaje de postulación:', error);
      throw error;
    }
  }
};